import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Tooltip,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  Send as SendIcon,
  History as HistoryIcon,
  Description as TemplateIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Preview as PreviewIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Sms as SmsIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from 'notistack';
import api from '../api/api';
import PageHeader from '../components/PageHeader';

interface MessageTemplate {
  id: number;
  name: string;
  subject: string;
  content: string;
  type: 'email' | 'whatsapp' | 'sms';
  category: string;
  created_at: string;
}

interface CommunicationHistory {
  id: number;
  type: 'email' | 'whatsapp' | 'sms';
  recipient: string;
  subject: string;
  content: string;
  status: 'sent' | 'failed' | 'pending';
  sent_at: string;
  error_message?: string;
}

interface RecipientGroup {
  id: number;
  name: string;
  type: 'school_admins' | 'all_users' | 'custom';
  recipients: string[];
  created_at: string;
}

const Communications: React.FC = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  
  // State
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [history, setHistory] = useState<CommunicationHistory[]>([]);
  const [recipientGroups, setRecipientGroups] = useState<RecipientGroup[]>([]);
  const [schools, setSchools] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  
  // Message composition
  const [messageType, setMessageType] = useState<'email' | 'whatsapp' | 'sms'>('email');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [recipientType, setRecipientType] = useState<'individual' | 'group' | 'bulk'>('individual');
  const [recipients, setRecipients] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [enableWhatsApp, setEnableWhatsApp] = useState(false);
  const [enableEmail, setEnableEmail] = useState(true);
  const [enableSMS, setEnableSMS] = useState(false);
  const [smsConfigStatus, setSmsConfigStatus] = useState<any>(null);
  
  // Dialogs
  const [templateDialog, setTemplateDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [sending, setSending] = useState(false);

  // Template management
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    content: '',
    type: 'email' as 'email' | 'whatsapp' | 'sms',
    category: ''
  });

  useEffect(() => {
    if (user?.role === 'superuser') {
      fetchData();
      checkSmsConfiguration();
    }
  }, [user]);

  const checkSmsConfiguration = async () => {
    try {
      const response = await api.get('/settings/value/sms_enabled');
      const smsEnabled = (response.data as any).value === 'true';
      setEnableSMS(smsEnabled);
      
      if (smsEnabled) {
        // Get SMS configuration status
        const configResponse = await api.get('/settings/value/sms_provider');
        setSmsConfigStatus({
          enabled: smsEnabled,
          provider: (configResponse.data as any).value
        });
      }
    } catch (error) {
      console.error('Error checking SMS configuration:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Determine the correct endpoints based on user role
      const historyEndpoint = user?.role === 'superuser' 
        ? '/communications/history' 
        : '/communications/school/history';
      
      const templatesEndpoint = user?.role === 'superuser' 
        ? '/communications/templates' 
        : '/communications/school/templates';
      
      // Fetch templates, history, schools, and users
      const [templatesRes, historyRes, schoolsRes, usersRes] = await Promise.all([
        api.get(templatesEndpoint),
        api.get(historyEndpoint),
        api.get('/schools/'),
        api.get('/users/')
      ]);
      
      setTemplates((templatesRes.data as MessageTemplate[]) || []);
      setHistory((historyRes.data as CommunicationHistory[]) || []);
      setSchools((schoolsRes.data as any[]) || []);
      setUsers((usersRes.data as any[]) || []);
    } catch (error: any) {
      enqueueSnackbar('Failed to fetch communications data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id.toString() === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      setSubject(template.subject);
      setContent(template.content);
      setMessageType(template.type);
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      subject: '',
      content: '',
      type: 'email',
      category: ''
    });
    setTemplateDialog(true);
  };

  const handleEditTemplate = (template: MessageTemplate) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      subject: template.subject || '',
      content: template.content,
      type: template.type,
      category: template.category || ''
    });
    setTemplateDialog(true);
  };

  const handleDeleteTemplate = async (templateId: number) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        // Determine the correct template endpoint based on user role
        const templatesEndpoint = user?.role === 'superuser' 
          ? '/communications/templates' 
          : '/communications/school/templates';
        
        await api.delete(`${templatesEndpoint}/${templateId}`);
        enqueueSnackbar('Template deleted successfully', { variant: 'success' });
        fetchData();
      } catch (error: any) {
        enqueueSnackbar('Failed to delete template', { variant: 'error' });
      }
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.content) {
      enqueueSnackbar('Name and content are required', { variant: 'error' });
      return;
    }

    try {
      // Determine the correct template endpoint based on user role
      const templatesEndpoint = user?.role === 'superuser' 
        ? '/communications/templates' 
        : '/communications/school/templates';
      
      if (editingTemplate) {
        await api.put(`${templatesEndpoint}/${editingTemplate.id}`, templateForm);
        enqueueSnackbar('Template updated successfully', { variant: 'success' });
      } else {
        await api.post(templatesEndpoint, templateForm);
        enqueueSnackbar('Template created successfully', { variant: 'success' });
      }
      setTemplateDialog(false);
      fetchData();
    } catch (error: any) {
      enqueueSnackbar('Failed to save template', { variant: 'error' });
    }
  };

  const handlePreviewTemplate = (template: MessageTemplate) => {
    setSubject(template.subject || '');
    setContent(template.content);
    setMessageType(template.type);
    setActiveTab(0); // Switch to send message tab
  };

  const handleSendMessage = async () => {
    if (!recipients.length || (!subject && messageType === 'email') || !content) {
      enqueueSnackbar('Please fill in all required fields', { variant: 'error' });
      return;
    }

    if (!enableEmail && !enableWhatsApp && !enableSMS) {
      enqueueSnackbar('At least one communication method must be enabled', { variant: 'error' });
      return;
    }

    setSending(true);
    try {
      const payload = {
        type: messageType,
        recipients,
        subject: messageType === 'email' ? subject : undefined,
        content,
        enable_email: enableEmail,
        enable_whatsapp: enableWhatsApp,
        enable_sms: enableSMS
      };

      const response = await api.post('/communications/send', payload);
      
      if ((response.data as any).success) {
        enqueueSnackbar('Message sent successfully', { variant: 'success' });
        // Reset form
        setRecipients([]);
        setSubject('');
        setContent('');
        setSelectedTemplate('');
        // Refresh history
        fetchData();
      } else {
        enqueueSnackbar('Failed to send message', { variant: 'error' });
      }
    } catch (error: any) {
      enqueueSnackbar('Failed to send message', { variant: 'error' });
    } finally {
      setSending(false);
    }
  };

  const getRecipientOptions = () => {
    const options = [];
    
    // School admins
    const schoolAdmins = users.filter(u => u.role === 'admin');
    options.push({
      label: 'All School Admins',
      value: 'school_admins',
      count: schoolAdmins.length
    });
    
    // All users
    options.push({
      label: 'All Users',
      value: 'all_users',
      count: users.length
    });
    
    // Individual schools
    schools.forEach(school => {
      const schoolUsers = users.filter(u => u.school_id === school.id);
      options.push({
        label: `${school.name} (${schoolUsers.length} users)`,
        value: `school_${school.id}`,
        count: schoolUsers.length
      });
    });
    
    return options;
  };

  const handleRecipientTypeChange = (type: string) => {
    setRecipientType(type as any);
    setRecipients([]);
  };

  const handleRecipientSelection = (recipientType: string) => {
    let selectedRecipients: string[] = [];
    
    switch (recipientType) {
      case 'school_admins':
        if (enableEmail && enableWhatsApp) {
          // Include both emails and phone numbers
          const adminUsers = users.filter(u => u.role === 'admin');
          selectedRecipients = adminUsers.map(u => u.email);
          // Add phone numbers if available (assuming there's a phone field)
          adminUsers.forEach(u => {
            if (u.phone) selectedRecipients.push(u.phone);
          });
        } else if (enableEmail) {
          selectedRecipients = users
            .filter(u => u.role === 'admin')
            .map(u => u.email);
        } else if (enableWhatsApp) {
          // For WhatsApp, we need phone numbers - this would need to be implemented
          // For now, we'll show a message that phone numbers are needed
          selectedRecipients = [];
        }
        break;
      case 'all_users':
        if (enableEmail && enableWhatsApp) {
          selectedRecipients = users.map(u => u.email);
          // Add phone numbers if available
          users.forEach(u => {
            if (u.phone) selectedRecipients.push(u.phone);
          });
        } else if (enableEmail) {
          selectedRecipients = users.map(u => u.email);
        } else if (enableWhatsApp) {
          // For WhatsApp, we need phone numbers
          selectedRecipients = [];
        }
        break;
      default:
        if (recipientType.startsWith('school_')) {
          const schoolId = recipientType.replace('school_', '');
          const schoolUsers = users.filter(u => u.school_id === parseInt(schoolId));
          
          if (enableEmail && enableWhatsApp) {
            selectedRecipients = schoolUsers.map(u => u.email);
            // Add phone numbers if available
            schoolUsers.forEach(u => {
              if (u.phone) selectedRecipients.push(u.phone);
            });
          } else if (enableEmail) {
            selectedRecipients = schoolUsers.map(u => u.email);
          } else if (enableWhatsApp) {
            // For WhatsApp, we need phone numbers
            selectedRecipients = [];
          }
        }
    }
    
    setRecipients(selectedRecipients);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'success';
      case 'failed': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent': return <CheckCircleIcon color="success" />;
      case 'failed': return <ErrorIcon color="error" />;
      case 'pending': return <ScheduleIcon color="warning" />;
      default: return <ScheduleIcon />;
    }
  };

  if (user?.role !== 'superuser') {
    return <Alert severity="error">You are not authorized to view this page.</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Communications Center"
        subtitle="Send messages via email and WhatsApp"
        action={
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
          >
            Refresh
          </Button>
        }
      />

      <Paper sx={{ width: '100%' }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Send Message" icon={<SendIcon />} iconPosition="start" />
          <Tab label="Templates" icon={<TemplateIcon />} iconPosition="start" />
          <Tab label="History" icon={<HistoryIcon />} iconPosition="start" />
        </Tabs>

        {/* Send Message Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Compose Message</Typography>
            
            {/* Message Type Selection */}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableEmail}
                    onChange={(e) => setEnableEmail(e.target.checked)}
                  />
                }
                label="Send via Email"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={enableWhatsApp}
                    onChange={(e) => setEnableWhatsApp(e.target.checked)}
                  />
                }
                label="Send via WhatsApp"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={enableSMS}
                    onChange={(e) => setEnableSMS(e.target.checked)}
                    disabled={!smsConfigStatus?.enabled}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SmsIcon />
                    Send via SMS
                    {!smsConfigStatus?.enabled && (
                      <Tooltip title="SMS is not configured. Configure it in System Settings.">
                        <ErrorIcon color="warning" fontSize="small" />
                      </Tooltip>
                    )}
                  </Box>
                }
              />
            </Box>

            {/* Template Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Use Template (Optional)</InputLabel>
              <Select
                value={selectedTemplate}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                label="Use Template (Optional)"
              >
                <MenuItem value="">No Template</MenuItem>
                {templates.map(template => (
                  <MenuItem key={template.id} value={template.id.toString()}>
                    {template.name} ({template.type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Recipients */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Recipient Type</InputLabel>
              <Select
                value={recipientType}
                onChange={(e) => handleRecipientTypeChange(e.target.value)}
                label="Recipient Type"
              >
                <MenuItem value="individual">Individual Recipients</MenuItem>
                <MenuItem value="group">Recipient Groups</MenuItem>
                <MenuItem value="bulk">Bulk Selection</MenuItem>
              </Select>
            </FormControl>

            {recipientType === 'bulk' && (
              <>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Select Recipients</InputLabel>
                  <Select
                    value=""
                    onChange={(e) => handleRecipientSelection(e.target.value)}
                    label="Select Recipients"
                  >
                    {getRecipientOptions().map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label} ({option.count} recipients)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {enableWhatsApp && !enableEmail && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      <strong>Note:</strong> WhatsApp messaging requires phone numbers. 
                      The bulk selection options currently only include email addresses from the user database. 
                      For WhatsApp messages, please use "Individual Recipients" and enter phone numbers manually.
                    </Typography>
                  </Alert>
                )}
                
                {enableSMS && !enableEmail && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      <strong>Note:</strong> SMS messaging requires phone numbers. 
                      The bulk selection options currently only include email addresses from the user database. 
                      For SMS messages, please use "Individual Recipients" and enter phone numbers manually.
                    </Typography>
                  </Alert>
                )}
              </>
            )}

            {recipientType === 'individual' && (
              <TextField
                fullWidth
                label={
                  enableEmail && (enableWhatsApp || enableSMS)
                    ? "Recipients (comma-separated emails or phone numbers)" 
                    : enableEmail 
                      ? "Recipients (comma-separated emails)" 
                      : "Recipients (comma-separated phone numbers)"
                }
                value={recipients.join(', ')}
                onChange={(e) => setRecipients(e.target.value.split(',').map(r => r.trim()).filter(r => r))}
                sx={{ mb: 3 }}
                placeholder={
                  enableEmail && (enableWhatsApp || enableSMS)
                    ? "email1@example.com, +254712345678, email2@example.com" 
                    : enableEmail 
                      ? "email1@example.com, email2@example.com" 
                      : "+254712345678, +254798765432"
                }
                helperText={
                  enableEmail && (enableWhatsApp || enableSMS)
                    ? "Enter email addresses for email messages and phone numbers for WhatsApp/SMS messages" 
                    : enableEmail 
                      ? "Enter valid email addresses (e.g., user@example.com)" 
                      : "Enter valid phone numbers with country code (e.g., +254712345678)"
                }
              />
            )}

            {/* Message Content */}
            {enableEmail && (
              <TextField
                fullWidth
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                sx={{ mb: 3 }}
                required
              />
            )}

            <TextField
              fullWidth
              label="Message Content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              multiline
              rows={6}
              sx={{ mb: 3 }}
              required
              placeholder={messageType === 'whatsapp' ? 'Enter your WhatsApp message...' : 'Enter your email message...'}
            />

            {/* Recipient Summary */}
            {recipients.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Recipients ({recipients.length}):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {recipients.slice(0, 5).map((recipient, index) => (
                    <Chip key={index} label={recipient} size="small" />
                  ))}
                  {recipients.length > 5 && (
                    <Chip label={`+${recipients.length - 5} more`} size="small" />
                  )}
                </Box>
              </Box>
            )}

            {/* Send Button */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
                onClick={handleSendMessage}
                disabled={sending || recipients.length === 0 || !content}
              >
                {sending ? 'Sending...' : 'Send Message'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={() => setPreviewDialog(true)}
                disabled={!content}
              >
                Preview
              </Button>
            </Box>
          </Box>
        )}

        {/* Templates Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Message Templates</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateTemplate}
              >
                New Template
              </Button>
            </Box>

            {templates.length === 0 ? (
              <Typography color="textSecondary">No templates found. Create your first template!</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {templates.map(template => (
                  <Card key={template.id}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="h6">{template.name}</Typography>
                          <Typography variant="body2" color="textSecondary">
                            {template.type === 'email' ? 'Email Template' : template.type === 'whatsapp' ? 'WhatsApp Template' : 'SMS Template'}
                          </Typography>
                          {template.type === 'email' && (
                            <Typography variant="body2" color="textSecondary">
                              Subject: {template.subject}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {template.content.substring(0, 100)}...
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton size="small" onClick={() => handleEditTemplate(template)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteTemplate(template.id)}>
                            <DeleteIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => handlePreviewTemplate(template)}>
                            <PreviewIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* History Tab */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Communication History</Typography>
            
            {history.length === 0 ? (
              <Typography color="textSecondary">No communication history found.</Typography>
            ) : (
              <List>
                {history.map(item => (
                  <ListItem key={item.id} divider>
                    <ListItemIcon>
                      {item.type === 'email' ? <EmailIcon /> : item.type === 'whatsapp' ? <WhatsAppIcon /> : <SmsIcon />}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">
                            {item.type === 'email' ? item.subject : item.type === 'whatsapp' ? 'WhatsApp Message' : 'SMS Message'}
                          </Typography>
                          {getStatusIcon(item.status)}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2">
                            To: {item.recipient}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Sent: {new Date(item.sent_at).toLocaleString()}
                          </Typography>
                          {item.error_message && (
                            <Typography variant="body2" color="error">
                              Error: {item.error_message}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </Paper>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Message Preview</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {enableEmail && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>Email Preview</Typography>
                <Typography variant="subtitle2" color="textSecondary">Subject:</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{subject}</Typography>
                <Typography variant="subtitle2" color="textSecondary">Content:</Typography>
                <Typography variant="body1">{content}</Typography>
              </Box>
            )}
            {enableWhatsApp && (
              <Box>
                <Typography variant="h6" gutterBottom>WhatsApp Preview</Typography>
                <Typography variant="body1">{content}</Typography>
              </Box>
            )}
            {enableSMS && (
              <Box>
                <Typography variant="h6" gutterBottom>SMS Preview</Typography>
                <Typography variant="body1">{content}</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={templateDialog} onClose={() => setTemplateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create New Template'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Template Name"
              value={templateForm.name}
              onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Subject (Optional)"
              value={templateForm.subject}
              onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Message Content"
              value={templateForm.content}
              onChange={(e) => setTemplateForm({ ...templateForm, content: e.target.value })}
              multiline
              rows={6}
              sx={{ mb: 2 }}
              required
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Template Type</InputLabel>
              <Select
                value={templateForm.type}
                onChange={(e) => setTemplateForm({ ...templateForm, type: e.target.value as 'email' | 'whatsapp' | 'sms' })}
                label="Template Type"
              >
                <MenuItem value="email">Email Template</MenuItem>
                <MenuItem value="whatsapp">WhatsApp Template</MenuItem>
                <MenuItem value="sms">SMS Template</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Category (Optional)</InputLabel>
              <Select
                value={templateForm.category}
                onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                label="Category"
              >
                <MenuItem value="">No Category</MenuItem>
                <MenuItem value="announcement">Announcement</MenuItem>
                <MenuItem value="event">Event</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
                <MenuItem value="general">General</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTemplate}>
            {editingTemplate ? 'Update Template' : 'Create Template'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Communications; 